/**
 * Browser-side moderation clip export. Takes the AudioEngine's 30-second
 * per-speaker ring snapshot, encodes each speaker's Float32 mono buffer
 * as a 16-bit PCM WAV, bundles them into a STORED-mode ZIP, and triggers
 * a download.
 *
 * STORED-mode ZIP (no compression) is chosen deliberately:
 *   - PCM WAVs don't compress well anyway (typical ratio < 0.95).
 *   - Avoids adding a deflate dependency or shipping pako.
 *   - The ZIP container itself gives admins a single file to attach to
 *     a Discord report or bug ticket.
 *
 * The ZIP format spec (PKZIP APPNOTE.TXT) is small enough that a hand-
 * rolled writer fits in ~80 lines.
 */

import type { ClipBufferSnapshot } from './audio.ts'

interface ZipEntry {
  readonly name: string
  readonly data: Uint8Array
  readonly crc32: number
  readonly modifiedDosTime: number
  readonly modifiedDosDate: number
}

/**
 * Encode a mono Float32Array as a 16-bit PCM WAV. Samples outside [-1, 1]
 * are clipped (not normalised) so the file represents what was actually
 * heard, distortion included.
 */
export function encodeWav(samples: Float32Array, sampleRate: number): Uint8Array {
  const numSamples = samples.length
  const byteRate = sampleRate * 2
  const dataSize = numSamples * 2
  const out = new ArrayBuffer(44 + dataSize)
  const view = new DataView(out)

  writeAscii(view, 0, 'RIFF')
  view.setUint32(4, 36 + dataSize, true)
  writeAscii(view, 8, 'WAVE')

  writeAscii(view, 12, 'fmt ')
  view.setUint32(16, 16, true)         // fmt chunk size
  view.setUint16(20, 1, true)          // PCM
  view.setUint16(22, 1, true)          // mono
  view.setUint32(24, sampleRate, true)
  view.setUint32(28, byteRate, true)
  view.setUint16(32, 2, true)          // block align
  view.setUint16(34, 16, true)         // bits per sample

  writeAscii(view, 36, 'data')
  view.setUint32(40, dataSize, true)

  let offset = 44
  for (let i = 0; i < numSamples; i++) {
    const clipped = Math.max(-1, Math.min(1, samples[i]!))
    view.setInt16(offset, Math.round(clipped * 0x7fff), true)
    offset += 2
  }
  return new Uint8Array(out)
}

function writeAscii(view: DataView, offset: number, str: string): void {
  for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i))
}

/**
 * Build a STORED-mode ZIP from a list of name+bytes pairs. Returns a Blob
 * suitable for an anchor-download.
 */
export function buildZip(files: ReadonlyArray<{ name: string; data: Uint8Array }>): Blob {
  const now = new Date()
  const dosTime = ((now.getHours() & 0x1f) << 11)
    | ((now.getMinutes() & 0x3f) << 5)
    | ((Math.floor(now.getSeconds() / 2)) & 0x1f)
  const dosDate = (((now.getFullYear() - 1980) & 0x7f) << 9)
    | (((now.getMonth() + 1) & 0x0f) << 5)
    | (now.getDate() & 0x1f)

  const entries: ZipEntry[] = files.map((f) => ({
    name: f.name,
    data: f.data,
    crc32: crc32(f.data),
    modifiedDosTime: dosTime,
    modifiedDosDate: dosDate,
  }))

  // First pass: compute local headers and total size for the central dir.
  const localChunks: Uint8Array[] = []
  const centralChunks: Uint8Array[] = []
  let offset = 0
  const localOffsets: number[] = []
  for (const e of entries) {
    localOffsets.push(offset)
    const nameBytes = new TextEncoder().encode(e.name)
    const header = new Uint8Array(30 + nameBytes.length)
    const hv = new DataView(header.buffer)
    hv.setUint32(0, 0x04034b50, true)        // local file header sig
    hv.setUint16(4, 20, true)                // version needed
    hv.setUint16(6, 0, true)                 // flags
    hv.setUint16(8, 0, true)                 // method: stored
    hv.setUint16(10, e.modifiedDosTime, true)
    hv.setUint16(12, e.modifiedDosDate, true)
    hv.setUint32(14, e.crc32, true)
    hv.setUint32(18, e.data.length, true)    // compressed size
    hv.setUint32(22, e.data.length, true)    // uncompressed size
    hv.setUint16(26, nameBytes.length, true)
    hv.setUint16(28, 0, true)                // extra field length
    header.set(nameBytes, 30)
    localChunks.push(header, e.data)
    offset += header.length + e.data.length
  }
  const centralDirStart = offset
  for (let i = 0; i < entries.length; i++) {
    const e = entries[i]!
    const nameBytes = new TextEncoder().encode(e.name)
    const cd = new Uint8Array(46 + nameBytes.length)
    const cv = new DataView(cd.buffer)
    cv.setUint32(0, 0x02014b50, true)        // central dir sig
    cv.setUint16(4, 20, true)                // version made by
    cv.setUint16(6, 20, true)                // version needed
    cv.setUint16(8, 0, true)                 // flags
    cv.setUint16(10, 0, true)                // method
    cv.setUint16(12, e.modifiedDosTime, true)
    cv.setUint16(14, e.modifiedDosDate, true)
    cv.setUint32(16, e.crc32, true)
    cv.setUint32(20, e.data.length, true)
    cv.setUint32(24, e.data.length, true)
    cv.setUint16(28, nameBytes.length, true)
    cv.setUint16(30, 0, true)                // extra field length
    cv.setUint16(32, 0, true)                // comment length
    cv.setUint16(34, 0, true)                // disk number
    cv.setUint16(36, 0, true)                // internal attrs
    cv.setUint32(38, 0, true)                // external attrs
    cv.setUint32(42, localOffsets[i]!, true) // local header offset
    cd.set(nameBytes, 46)
    centralChunks.push(cd)
    offset += cd.length
  }
  const centralDirSize = offset - centralDirStart
  const eocd = new Uint8Array(22)
  const ev = new DataView(eocd.buffer)
  ev.setUint32(0, 0x06054b50, true)          // EOCD sig
  ev.setUint16(4, 0, true)                   // disk number
  ev.setUint16(6, 0, true)                   // disk with central dir
  ev.setUint16(8, entries.length, true)      // entries on this disk
  ev.setUint16(10, entries.length, true)     // total entries
  ev.setUint32(12, centralDirSize, true)
  ev.setUint32(16, centralDirStart, true)
  ev.setUint16(20, 0, true)                  // comment length

  const parts: BlobPart[] = [...localChunks, ...centralChunks, eocd] as unknown as BlobPart[]
  return new Blob(parts, { type: 'application/zip' })
}

/** CRC-32 with the standard polynomial 0xEDB88320. */
function crc32(bytes: Uint8Array): number {
  let crc = 0xffffffff
  const table = crc32Table()
  for (let i = 0; i < bytes.length; i++) {
    crc = (crc >>> 8) ^ table[(crc ^ bytes[i]!) & 0xff]!
  }
  return (crc ^ 0xffffffff) >>> 0
}

let CRC_TABLE: Uint32Array | null = null
function crc32Table(): Uint32Array {
  if (CRC_TABLE !== null) return CRC_TABLE
  const t = new Uint32Array(256)
  for (let i = 0; i < 256; i++) {
    let c = i
    for (let k = 0; k < 8; k++) {
      c = (c & 1) !== 0 ? (0xedb88320 ^ (c >>> 1)) : (c >>> 1)
    }
    t[i] = c >>> 0
  }
  CRC_TABLE = t
  return t
}

/**
 * Build a moderation zip from a clip-buffer snapshot. Filenames embed the
 * speaker label (sanitised) + AGID so admins can identify who said what.
 * Includes a manifest.json describing the capture for debugging.
 */
export function buildClipZip(
  snapshot: import('./audio.ts').ClipBufferSnapshot,
  labelFor: (agid: string) => string,
): Blob {
  const files: Array<{ name: string; data: Uint8Array }> = []
  const manifest: {
    capturedAt: string
    sampleRate: number
    durationSeconds: number
    speakers: Array<{ agid: string; name: string; samples: number; file: string }>
  } = {
    capturedAt: snapshot.capturedAt.toISOString(),
    sampleRate: snapshot.sampleRate,
    durationSeconds: snapshot.durationSeconds,
    speakers: [],
  }
  for (const [agid, samples] of snapshot.perSpeaker) {
    const safeName = sanitiseFilenamePart(labelFor(agid))
    const safeAgid = sanitiseFilenamePart(agid)
    const fileName = `${safeName}-${safeAgid}.wav`
    files.push({ name: fileName, data: encodeWav(samples, snapshot.sampleRate) })
    manifest.speakers.push({ agid, name: labelFor(agid), samples: samples.length, file: fileName })
  }
  files.push({
    name: 'manifest.json',
    data: new TextEncoder().encode(JSON.stringify(manifest, null, 2)),
  })
  return buildZip(files)
}

export function clipZipFilename(snapshot: ClipBufferSnapshot): string {
  return `aav-clip-${isoFileStamp(snapshot.capturedAt)}.zip`
}

function isoFileStamp(d: Date): string {
  const pad = (n: number): string => n.toString().padStart(2, '0')
  return `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}Z`
}

function sanitiseFilenamePart(s: string): string {
  // Trim, collapse whitespace, drop characters that are illegal on Windows
  // or in ZIP filenames. Leaves a small ASCII subset that's safe to embed
  // in a downloadable artifact regardless of admin's OS.
  const cleaned = s.replace(/[\\/:*?"<>|]/g, '').replace(/\s+/g, '_').trim()
  if (cleaned.length === 0) return 'speaker'
  return cleaned.slice(0, 60)
}

/** Trigger a browser download for the given blob + suggested filename. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  // Revoke after a tick so Safari has time to consume the URL.
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
